import { SupabaseClient } from "@supabase/supabase-js";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import type { Database } from "../../supabase.types";
import type { VideoInsert, VideoRow } from "../../models/videos";

export type { VideoInsert, VideoRow };

export async function insertVideoPipelineRecord(
  insert: VideoInsert
): Promise<VideoRow> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from("videos")
    .insert(insert)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Marks GCP-pipeline video rows stuck in the initial "uploading" state as
 * failed once they predate the cutoff.
 *
 * Only targets rows the orchestrator never picked up — transcode_status still
 * "pending" — which means the browser upload never finalized (no GCS
 * object-finalize event fired). Videos mid-transcode also keep
 * overall_status = "uploading" but move to transcode_status = "processing", so
 * they are intentionally excluded and never reaped while still in flight.
 *
 * Returns the ids that were reaped.
 */
export async function markStaleUploadingVideosFailed(
  supabase: SupabaseClient<Database>,
  staleBeforeIso: string
): Promise<string[]> {
  const { data, error } = await supabase
    .from("videos")
    .update({ overall_status: "failed" })
    .eq("overall_status", "uploading")
    .eq("transcode_status", "pending")
    .eq("pipeline_complete", false)
    .lt("created_at", staleBeforeIso)
    .select("id");

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => row.id);
}

/**
 * Returns GCP-pipeline videos handed off to Cloudflare (cloudflare_video_id set)
 * but not yet marked pipeline_complete — i.e. Cloudflare is still transcoding.
 * The publish cron polls each of these for readiness.
 */
export async function getVideosAwaitingCloudflare(
  supabase: SupabaseClient<Database>
): Promise<{ id: string; cloudflare_video_id: string }[]> {
  const { data, error } = await supabase
    .from("videos")
    .select("id, cloudflare_video_id")
    .eq("overall_status", "processing")
    .eq("pipeline_complete", false)
    .not("cloudflare_video_id", "is", null);

  if (error) {
    throw error;
  }

  return (data ?? [])
    .filter((row): row is { id: string; cloudflare_video_id: string } =>
      Boolean(row.cloudflare_video_id)
    )
    .map((row) => ({
      id: row.id,
      cloudflare_video_id: row.cloudflare_video_id,
    }));
}

/**
 * Flips a video into the publishable state once Cloudflare reports it is ready
 * to stream. Optionally records the duration the readiness check reported.
 */
export async function markVideoPipelineComplete(
  supabase: SupabaseClient<Database>,
  videoId: string,
  durationSeconds?: number | null
): Promise<void> {
  const { error } = await supabase
    .from("videos")
    .update({
      pipeline_complete: true,
      overall_status: "ready_to_publish",
      // Cloudflare reports fractional seconds (27.04); the column is integer.
      ...(durationSeconds != null
        ? { duration_seconds: Math.round(durationSeconds) }
        : {}),
    })
    .eq("id", videoId);

  if (error) {
    throw error;
  }
}

/**
 * Fetch the pipeline `overall_status` for a set of videos, keyed by video id.
 *
 * The `videos` table has RLS enabled with no policies — it is service-role only
 * (see the create_videos_table migration) — so a comedian-manager's RLS-scoped
 * client cannot read it, and embedding a `videos(...)` join in a user-scoped
 * specials query silently returns null. Callers must pass the service-role
 * client and MUST scope `videoIds` to rows they have already authorized (e.g.
 * the punchup_video_ids of a comedian's own specials).
 */
export async function getVideoStatusesByIds(
  supabase: SupabaseClient<Database>,
  videoIds: string[]
): Promise<Map<string, string>> {
  if (videoIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("videos")
    .select("id, overall_status")
    .in("id", videoIds);

  if (error) {
    throw error;
  }

  return new Map((data ?? []).map((v) => [v.id, v.overall_status]));
}
